import React, { useRef, useState } from "react";
import "./CreatorWorkSpace.css";
import PromptBar from "./PromptBar";

/**
 * CreatorWorkspace
 * - Left rail: navigation + asset library
 * - Top bar: search, quick actions, profile
 * - Center: preview canvas + timeline (compact)
 * - Right rail: tool panels (Generate / Edit / Export)
 * - Bottom: render queue + status
 *
 * NOTE: All actions are wired to placeholders you can connect to your backend/AI.
 */
export default function CreatorWorkspace() {
  const [activeNav, setActiveNav] = useState("workspace");
  const [activeTool, setActiveTool] = useState("generate");
  const [genTab, setGenTab] = useState("video");
  const [editTab, setEditTab] = useState("adjust");
  const [exportOpen, setExportOpen] = useState(false);

  const [assets, setAssets] = useState([]);
  const [queue, setQueue] = useState([]);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const fileInputRef = useRef(null);

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files || []);
    ingestFiles(files);
  };

  const handleFilePick = (e) => {
    const files = Array.from(e.target.files || []);
    ingestFiles(files);
  };

  const ingestFiles = (files) => {
    const mapped = files.map((f) => ({
      id: `${f.name}-${Date.now()}`,
      name: f.name,
      type: f.type.startsWith("video")
        ? "video"
        : f.type.startsWith("image")
        ? "image"
        : f.type.startsWith("audio")
        ? "audio"
        : "file",
      size: f.size,
      url: URL.createObjectURL(f),
    }));
    setAssets((prev) => [...mapped, ...prev]);
    if (!previewSrc && mapped[0]) setPreviewSrc(mapped[0].url);
  };

  const addToQueue = (task) => {
    const id = `task_${Date.now()}`;
    const item = { id, status: "queued", ...task };
    setQueue((q) => [item, ...q]);
    // Simulate progress
    setTimeout(() => updateQueue(id, "processing"), 900);
    setTimeout(() => updateQueue(id, "done"), 3500);
  };

  const updateQueue = (id, status) => {
    setQueue((q) => q.map((t) => (t.id === id ? { ...t, status } : t)));
  };

  const handleGenerate = () => {
    addToQueue({
      kind: "generate",
      details: `${genTab} generation`,
    });
  };

  const handleApplyEdit = (label) => {
    addToQueue({
      kind: "edit",
      details: label,
    });
  };

  const handleExport = () => {
    setExportOpen(false);
    addToQueue({ kind: "export", details: "Export & Social Post" });
  };

  const onShareClick = (platform) => {
    addToQueue({ kind: "share", details: `Post to ${platform}` });
  };

  const onAssetClick = (a) => setPreviewSrc(a.url);

  const handleSearch = async (query) => {
    if (!query.trim()) return;

    setIsSearching(true);
    try {
      const response = await fetch('/api/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query }),
        credentials: 'include',
      });

      if (response.ok) {
        const results = await response.json();
        setSearchResults(results);
      } else {
        console.error('Search failed');
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div id="CreatorWorkSpace" className="cw-shell">
      {/* Top Bar */}
      <header className="cw-topbar">
        <div className="cw-brand">
          <span className="logo-dot" />
          RapidMind Studio
          <span className="plan-badge">Pro</span>
        </div>
        <div className="cw-search">
          <input placeholder="Search projects, assets, prompts…" />
          <button className="btn ghost" onClick={() => fileInputRef.current?.click()}>
            ⭳ Import
          </button>
          <input
            type="file"
            multiple
            hidden
            ref={fileInputRef}
            onChange={handleFilePick}
            accept="image/*,video/*,audio/*"
          />
        </div>
        <div className="cw-actions">
          <button className="btn">+ New Project</button>
          <button className="btn ghost">Templates</button>
          <div className="cw-profile">
            <div className="avatar" />
            <span>Creator</span>
          </div>
        </div>
      </header>

      {/* Prompt Bar */}
      <div style={{ padding: '16px', background: '#f8f9fa' }}>
        <PromptBar onSearch={handleSearch} />
      </div>

      <div className="cw-main">
        {/* Left Rail */}
        <aside className="cw-rail">
          <nav className="cw-nav">
            {[
              { id: "workspace", label: "Workspace" },
              { id: "projects", label: "Projects" },
              { id: "assets", label: "Assets" },
              { id: "trends", label: "Trends" },
              { id: "integrations", label: "Integrations" },
              { id: "settings", label: "Settings" },
            ].map((n) => (
              <button
                key={n.id}
                className={`nav-item ${activeNav === n.id ? "active" : ""}`}
                onClick={() => setActiveNav(n.id)}
              >
                {n.label}
              </button>
            ))}
          </nav>

          <div className="cw-asset-head">
            <div>Library</div>
            <button className="link" onClick={() => fileInputRef.current?.click()}>
              Upload
            </button>
          </div>

          <div className="cw-asset-list">
            {assets.length === 0 ? (
              <div className="cw-empty">
                <p>No assets yet.</p>
                <p className="muted">Drag & drop files here or click Upload.</p>
              </div>
            ) : (
              assets.map((a) => (
                <button
                  key={a.id}
                  className={`asset ${a.type}`}
                  onClick={() => onAssetClick(a)}
                  title={a.name}
                >
                  <div className="asset-thumb">
                    {a.type === "image" && <span>🖼️</span>}
                    {a.type === "video" && <span>🎬</span>}
                    {a.type === "audio" && <span>🎧</span>}
                    {a.type === "file" && <span>📄</span>}
                  </div>
                  <div className="asset-meta">
                    <div className="name">{a.name}</div>
                    <div className="sub">{(a.size / 1024 / 1024).toFixed(1)} MB</div>
                  </div>
                </button>
              ))
            )}
          </div>
        </aside>

        {/* Canvas / Timeline */}
        <section className="cw-canvas-area" onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}>
          <div className="cw-drop-hint">Drop media anywhere to add</div>

          <div className="cw-canvas">
            {previewSrc ? (
              <div className="preview-frame">
                {/* Fallback <video> or <img> preview */}
                {previewSrc.match(/\.mp4|\.webm|video\//) ? (
                  <video src={previewSrc} controls />
                ) : previewSrc.match(/\.mp3|\.wav|audio\//) ? (
                  <div className="audio-preview">
                    <div className="audio-wave" />
                    <audio src={previewSrc} controls />
                  </div>
                ) : (
                  <img src={previewSrc} alt="Preview" />
                )}
              </div>
            ) : (
              <div className="cw-canvas-empty">
                <div className="big">🎯</div>
                <p>Start by uploading or generating content.</p>
              </div>
            )}
          </div>

          <div className="cw-timeline">
            <div className="track">
              <div className="track-label">Video</div>
              <div className="track-strip" />
            </div>
            <div className="track">
              <div className="track-label">Audio</div>
              <div className="track-strip dashed" />
            </div>
            <div className="track">
              <div className="track-label">Captions</div>
              <div className="track-strip dotted" />
            </div>
            <div className="tl-controls">
              <button className="btn ghost">⟲ Undo</button>
              <button className="btn ghost">⟳ Redo</button>
              <button className="btn" onClick={() => setExportOpen(true)}>
                Export
              </button>
            </div>
          </div>
        </section>

        {/* Right Rail: Tools */}
        <aside className="cw-tools">
          <div className="tabs">
            <button
              className={`tab ${activeTool === "generate" ? "active" : ""}`}
              onClick={() => setActiveTool("generate")}
            >
              Generate
            </button>
            <button
              className={`tab ${activeTool === "edit" ? "active" : ""}`}
              onClick={() => setActiveTool("edit")}
            >
              Edit
            </button>
            <button
              className={`tab ${activeTool === "export" ? "active" : ""}`}
              onClick={() => setActiveTool("export")}
            >
              Export & Post
            </button>
          </div>

          {activeTool === "generate" && (
            <div className="panel">
              <div className="segmented">
                {["video", "image", "voice"].map((t) => (
                  <button
                    key={t}
                    className={`chip ${genTab === t ? "active" : ""}`}
                    onClick={() => setGenTab(t)}
                  >
                    {t[0].toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>

              {genTab === "video" && (
                <div className="tool-block">
                  <label>Prompt</label>
                  <textarea placeholder="Describe the scene, style, pacing…" />
                  <div className="grid two">
                    <div>
                      <label>Duration</label>
                      <select>
                        <option>15s (TikTok/Shorts)</option>
                        <option>30s</option>
                        <option>60s</option>
                        <option>120s</option>
                      </select>
                    </div>
                    <div>
                      <label>Aspect</label>
                      <select>
                        <option>9:16 (Vertical)</option>
                        <option>1:1 (Square)</option>
                        <option>16:9 (Horizontal)</option>
                      </select>
                    </div>
                  </div>
                  <button className="btn primary" onClick={handleGenerate}>Generate Video</button>
                </div>
              )}

              {genTab === "image" && (
                <div className="tool-block">
                  <label>Prompt</label>
                  <textarea placeholder="Describe the image, mood, lighting, composition…" />
                  <div className="grid two">
                    <div>
                      <label>Size</label>
                      <select>
                        <option>1024×1024</option>
                        <option>768×1024</option>
                        <option>1280×720</option>
                      </select>
                    </div>
                    <div>
                      <label>Variation</label>
                      <select>
                        <option>Single</option>
                        <option>2 variations</option>
                        <option>4 variations</option>
                      </select>
                    </div>
                  </div>
                  <button className="btn primary" onClick={handleGenerate}>Generate Image</button>
                </div>
              )}

              {genTab === "voice" && (
                <div className="tool-block">
                  <label>Script</label>
                  <textarea placeholder="Paste or write your voiceover script…" />
                  <div className="grid two">
                    <div>
                      <label>Voice</label>
                      <select>
                        <option>Warm Narrator</option>
                        <option>Energetic Social</option>
                        <option>News Anchor</option>
                      </select>
                    </div>
                    <div>
                      <label>Speed</label>
                      <input type="range" min="0.75" max="1.25" step="0.05" defaultValue="1" />
                    </div>
                  </div>
                  <button className="btn primary" onClick={handleGenerate}>Synthesize Voice</button>
                </div>
              )}
            </div>
          )}

          {activeTool === "edit" && (
            <div className="panel">
              <div className="segmented">
                {["adjust", "background", "captions", "audio", "branding"].map((t) => (
                  <button
                    key={t}
                    className={`chip ${editTab === t ? "active" : ""}`}
                    onClick={() => setEditTab(t)}
                  >
                    {t[0].toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>

              {editTab === "adjust" && (
                <div className="tool-block">
                  <label>Basic Adjustments</label>
                  <div className="slider">
                    <span>Brightness</span>
                    <input type="range" min="0" max="200" defaultValue="100" />
                  </div>
                  <div className="slider">
                    <span>Contrast</span>
                    <input type="range" min="0" max="200" defaultValue="100" />
                  </div>
                  <div className="slider">
                    <span>Saturation</span>
                    <input type="range" min="0" max="200" defaultValue="100" />
                  </div>
                  <button className="btn" onClick={() => handleApplyEdit("Adjustments")}>
                    Apply
                  </button>
                </div>
              )}

              {editTab === "background" && (
                <div className="tool-block">
                  <label>Background Tools</label>
                  <div className="grid two">
                    <button className="btn ghost" onClick={() => handleApplyEdit("BG Cleanup (with rights)")}>
                      Background Cleanup
                    </button>
                    <button className="btn ghost" onClick={() => handleApplyEdit("Object Removal (with rights)")}>
                      Object Removal
                    </button>
                  </div>
                  <p className="legal">
                    Use only on assets you own or have permission to edit.
                  </p>
                </div>
              )}

              {editTab === "captions" && (
                <div className="tool-block">
                  <label>Auto-Captions</label>
                  <div className="grid two">
                    <div>
                      <label>Language</label>
                      <select>
                        <option>English</option>
                        <option>Arabic</option>
                        <option>French</option>
                      </select>
                    </div>
                    <div>
                      <label>Style</label>
                      <select>
                        <option>Karaoke</option>
                        <option>Classic</option>
                        <option>Bold Pop</option>
                      </select>
                    </div>
                  </div>
                  <button className="btn" onClick={() => handleApplyEdit("Auto-Captions")}>
                    Generate Captions
                  </button>
                </div>
              )}

              {editTab === "audio" && (
                <div className="tool-block">
                  <label>Audio Cleanup</label>
                  <div className="grid two">
                    <button className="btn ghost" onClick={() => handleApplyEdit("Noise Reduction")}>
                      Noise Reduction
                    </button>
                    <button className="btn ghost" onClick={() => handleApplyEdit("Loudness Normalize")}>
                      Loudness Normalize
                    </button>
                  </div>
                </div>
              )}

              {editTab === "branding" && (
                <div className="tool-block">
                  <label>Watermark Manager (Your Assets Only)</label>
                  <input placeholder="Watermark text or upload logo…" />
                  <div className="grid two">
                    <button className="btn ghost" onClick={() => handleApplyEdit("Add Watermark")}>
                      Add Watermark
                    </button>
                    <button className="btn ghost" onClick={() => handleApplyEdit("Remove Own Watermark")}>
                      Remove Own Watermark
                    </button>
                  </div>
                  <p className="legal">
                    Don’t remove third-party/copyright watermarks.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTool === "export" && (
            <div className="panel">
              <div className="tool-block">
                <label>Export Preset</label>
                <select>
                  <option>4K 60fps (H.264)</option>
                  <option>1080p 30fps (H.264)</option>
                  <option>Vertical 1080×1920 (H.264)</option>
                  <option>GIF / Web Optimized</option>
                </select>
              </div>

              <div className="tool-block">
                <label>Platforms</label>
                <div className="grid two">
                  {["YouTube", "TikTok", "Facebook", "Instagram", "X"].map((p) => (
                    <button key={p} className="btn ghost" onClick={() => onShareClick(p)}>
                      {p}
                    </button>
                  ))}
                </div>
                <div className="platform-extras">
                  <label className="checkbox">
                    <input type="checkbox" /> Add auto-hashtags
                  </label>
                  <label className="checkbox">
                    <input type="checkbox" /> Generate thumbnail
                  </label>
                  <label className="checkbox">
                    <input type="checkbox" /> Schedule post
                  </label>
                </div>
              </div>

              <div className="tool-block">
                <label>Also Export To</label>
                <div className="grid two">
                  <button className="btn ghost" onClick={() => addToQueue({ kind: "handoff", details: "Export to Canva" })}>
                    Open in Canva
                  </button>
                  <button className="btn ghost" onClick={() => addToQueue({ kind: "handoff", details: "Export to Drive" })}>
                    Google Drive
                  </button>
                </div>
              </div>

              <button className="btn primary" onClick={() => setExportOpen(true)}>Prepare Export</button>
            </div>
          )}
        </aside>
      </div>

      {/* Bottom Queue */}
      <footer className="cw-queue">
        <div className="q-head">Render Queue</div>
        <div className="q-list">
          {queue.length === 0 ? (
            <div className="q-empty">No jobs yet.</div>
          ) : (
            queue.map((t) => (
              <div key={t.id} className={`q-item ${t.status}`}>
                <div className="q-kind">{t.kind}</div>
                <div className="q-details">{t.details}</div>
                <div className="q-status">{t.status}</div>
              </div>
            ))
          )}
        </div>
      </footer>

      {/* Export Modal */}
      {exportOpen && (
        <div className="modal">
          <div className="modal-card">
            <div className="modal-head">
              <div>Export & Post</div>
              <button className="icon-btn" onClick={() => setExportOpen(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="grid two">
                <div>
                  <label>Filename</label>
                  <input defaultValue="rapidmind_export" />
                </div>
                <div>
                  <label>Format</label>
                  <select defaultValue="mp4">
                    <option value="mp4">MP4</option>
                    <option value="mov">MOV</option>
                    <option value="gif">GIF</option>
                    <option value="png">PNG (image)</option>
                    <option value="mp3">MP3 (audio)</option>
                  </select>
                </div>
              </div>
              <div className="grid two">
                <div>
                  <label>Bitrate</label>
                  <select>
                    <option>Auto</option>
                    <option>High</option>
                    <option>Medium</option>
                    <option>Low</option>
                  </select>
                </div>
                <div>
                  <label>Subtitles</label>
                  <select>
                    <option>Burned In</option>
                    <option>Sidecar (.srt)</option>
                    <option>None</option>
                  </select>
                </div>
              </div>
              <label className="checkbox">
                <input type="checkbox" /> Include thumbnail
              </label>
              <label className="checkbox">
                <input type="checkbox" /> Post automatically to selected platforms
              </label>
            </div>
            <div className="modal-foot">
              <button className="btn ghost" onClick={() => setExportOpen(false)}>Cancel</button>
              <button className="btn primary" onClick={handleExport}>Export Now</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
